import { SubscriptionPlan } from "../../../../services/organization-subscription-plans/SubscriptionPlan";
import { translationService } from "../../../../services/translation/TranslationService";
import { ModalContentStrategy } from "./ModalContentStrategy";
import { ModalElement, MessageContent } from "./ModalContentContext";
import { getLocalPrice } from "../../../../helpers/MathHelpers";

export class UpdatePlanContentBuilder implements ModalContentStrategy {
  private currentSeatsAmount: number;
  private updatedSeatsAmount: number;

  private currentExtraStorage: number;
  private updatedExtraStorage: number;
  private scheduledExtraStorage: number;

  private currentExtraVAs: number;
  private updatedExtraVAs: number;
  private scheduledExtraVAs: number;

  private quarterlyPayment: string;
  private nextPaymentDate: string;

  private areSeatsChanged: boolean;
  private areSeatsIncreased: boolean;
  private areSeatsDecreased: boolean;

  private isExtraStoragePackageChanged: boolean;
  private isExtraStoragePackageIncreased: boolean;
  private isExtraStoragePackageDecreased: boolean;

  private isExtraVAPackageChanged: boolean;
  private isExtraVAPackageIncreased: boolean;
  private isExtraVAPackageDecreased: boolean;

  private seatsDifference: number;
  private extraStoraceSpaceDifference: number;
  private extraVAPackageDifference: number;

  private canceledExtraStorageScheduled: boolean;
  private canceledExtraVAsScheduled: boolean;

  private updates: ModalElement[] = [];
  private invoice: ModalElement[] = [];
  private info: ModalElement[] = [];

  constructor(
    seatsAmount: number,
    extraStorageAmount: number,
    extraVAAmount: number,
    activeSubscription: SubscriptionPlan,
    scheduledSubscription: SubscriptionPlan | undefined,
    quarterlyPayment: number,
    nextPaymentDate: string
  ) {
    this.currentSeatsAmount = activeSubscription.activeSubscriptionPackageAmount || 0;
    this.updatedSeatsAmount = seatsAmount;

    this.currentExtraStorage =
      activeSubscription.extraStoragePackage?.activeSubscriptionPackageAmount || 0;
    this.updatedExtraStorage = extraStorageAmount;
    this.scheduledExtraStorage =
      scheduledSubscription?.extraStoragePackage?.activeSubscriptionPackageAmount ??
      this.currentExtraStorage;

    this.currentExtraVAs = activeSubscription.extraVACalls?.activeSubscriptionPackageAmount || 0;
    this.updatedExtraVAs = extraVAAmount;
    this.scheduledExtraVAs =
      scheduledSubscription?.extraVACalls?.activeSubscriptionPackageAmount ?? this.currentExtraVAs;

    this.quarterlyPayment = getLocalPrice(quarterlyPayment);
    this.nextPaymentDate = nextPaymentDate;

    this.areSeatsChanged = this.currentSeatsAmount !== this.updatedSeatsAmount;
    this.areSeatsIncreased = this.currentSeatsAmount < this.updatedSeatsAmount;
    this.areSeatsDecreased = this.currentSeatsAmount > this.updatedSeatsAmount;

    this.isExtraStoragePackageChanged = this.currentExtraStorage !== this.updatedExtraStorage;
    this.isExtraStoragePackageIncreased = this.currentExtraStorage < this.updatedExtraStorage;
    this.isExtraStoragePackageDecreased = this.currentExtraStorage > this.updatedExtraStorage;

    this.isExtraVAPackageChanged = this.currentExtraVAs !== this.updatedExtraVAs;
    this.isExtraVAPackageIncreased = this.currentExtraVAs < this.updatedExtraVAs;
    this.isExtraVAPackageDecreased = this.currentExtraVAs > this.updatedExtraVAs;

    this.seatsDifference = Math.abs(this.updatedSeatsAmount - this.currentSeatsAmount);
    this.extraStoraceSpaceDifference = Math.abs(
      this.updatedExtraStorage - this.currentExtraStorage
    );
    this.extraVAPackageDifference = Math.abs(this.updatedExtraVAs - this.currentExtraVAs);

    this.canceledExtraStorageScheduled =
      !this.isExtraStoragePackageChanged && this.scheduledExtraStorage !== this.currentExtraStorage;
    this.canceledExtraVAsScheduled =
      !this.isExtraVAPackageChanged && this.scheduledExtraVAs !== this.currentExtraVAs;
  }

  public buildModalContent(): MessageContent {
    this.init();

    this.updateMessageForCurrentBillingCycleChanges();

    this.updateMessageForNextBillingCycleChanges();

    this.end();

    return { updates: this.updates, invoice: this.invoice, info: this.info };
  }

  private init() {
    this.info.push({
      beforeText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-2-before"
      ),
      displayText: ` ${this.quarterlyPayment}€`,
      shouldDisplayBolded: true,
      afterText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-2-after"
      ),
    });
  }

  private updateMessageForCurrentBillingCycleChanges() {
    if (
      !this.areSeatsChanged &&
      !this.isExtraStoragePackageIncreased &&
      !this.isExtraVAPackageIncreased
    )
      return;

    this.initializeListForCurrentBillingCycle();

    this.updateMessageWhenSeatsIncreasedOrDecreased();

    if (this.isExtraStoragePackageIncreased) this.updateMessageWhenExtraStorageIncreased();

    if (this.isExtraVAPackageIncreased) this.updateMessageWhenExtraVAIncreased();

    this.endListForCurrentBillingCycle();
  }

  private initializeListForCurrentBillingCycle() {
    this.updates.push({
      displayText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-1"
      ),
      isStartList: true,
    });

    this.invoice.push({
      displayText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-3",
        { nextPaymentDate: this.nextPaymentDate }
      ),
      isStartList: true,
    });
  }

  private updateMessageWhenSeatsIncreasedOrDecreased() {
    if (this.areSeatsDecreased) {
      this.updates.push(
        {
          isStartItem: true,
        },
        { displayText: translationService.translate("general.from") },
        {
          beforeText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-before"
          ),
          displayText: ` ${this.currentSeatsAmount}${translationService.translate(
            "SubscriptionPlans.card.seats-general"
          )}`,
          shouldDisplayBolded: true,
          afterText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-after"
          ),
        },
        { displayText: translationService.translate("general.to") },
        {
          beforeText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-before"
          ),
          displayText: ` ${this.updatedSeatsAmount}${translationService.translate(
            "SubscriptionPlans.card.seats-general"
          )}`,
          shouldDisplayBolded: true,
          afterText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-after"
          ),
        },
        {
          isEndItem: true,
        }
      );

      this.invoice.push(
        {
          isStartItem: true,
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-5",
            { seatsDifference: this.seatsDifference }
          ),
        },
        {
          isEndItem: true,
        }
      );
    } else if (this.areSeatsIncreased) {
      this.updates.push(
        {
          isStartItem: true,
        },
        { displayText: translationService.translate("general.from") },
        {
          beforeText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-before"
          ),
          displayText: ` ${this.currentSeatsAmount}${translationService.translate(
            "SubscriptionPlans.card.seats-general"
          )}`,
          shouldDisplayBolded: true,
          afterText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-after"
          ),
        },
        { displayText: translationService.translate("general.to") },
        {
          beforeText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-before"
          ),
          displayText: ` ${this.updatedSeatsAmount}${translationService.translate(
            "SubscriptionPlans.card.seats-general"
          )}`,
          shouldDisplayBolded: true,
          afterText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-after"
          ),
        },
        {
          isEndItem: true,
        }
      );

      this.invoice.push(
        {
          isStartItem: true,
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-6",
            { seatsDifference: this.seatsDifference }
          ),
        },
        {
          isEndItem: true,
        }
      );
    }
  }

  private updateMessageWhenExtraStorageIncreased() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.currentExtraStorage}TB`,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
        shouldDisplayBolded: true,
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.updatedExtraStorage}TB`,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
        shouldDisplayBolded: true,
      },
      {
        isEndItem: true,
      }
    );

    this.invoice.push(
      {
        isStartItem: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-7"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
      },
      {
        displayText: ` ${this.extraStoraceSpaceDifference}TB`,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
      },
      {
        isEndItem: true,
      }
    );
  }

  private updateMessageWhenExtraVAIncreased() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.currentExtraVAs} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      {
        displayText: ` ${this.currentExtraVAs * 50}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.updatedExtraVAs} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      {
        displayText: ` ${this.updatedExtraVAs * 50}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      {
        isEndItem: true,
      }
    );

    this.invoice.push(
      {
        isStartItem: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-7"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-12",
          {
            vaDifference: this.extraVAPackageDifference,
            totalVACalls: this.extraVAPackageDifference * 50,
          }
        ),
      },
      {
        isEndItem: true,
      }
    );

    this.info.push(
      {
        isEmptyLine: true,
      },
      {
        isEmptyLine: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-15"
        ),
      }
    );
  }

  private endListForCurrentBillingCycle() {
    this.updates.push({
      isEndList: true,
    });

    this.invoice.push({
      isEndList: true,
    });
  }

  private updateMessageForNextBillingCycleChanges() {
    if (
      !this.isExtraStoragePackageDecreased &&
      !this.isExtraVAPackageDecreased &&
      !this.canceledExtraStorageScheduled &&
      !this.canceledExtraVAsScheduled
    )
      return;

    this.initializeListForNextBillingCycle();

    if (this.isExtraStoragePackageDecreased) this.updateMessageWhenExtraStorageDecreased();
    if (this.isExtraVAPackageDecreased) this.updateMessageWhenExtraVADecreased();

    if (this.canceledExtraStorageScheduled) this.updateMessageWhenCanceledExtraStorageScheduled();
    if (this.canceledExtraVAsScheduled) this.updateMessageWhenCanceledExtraVAsScheduled();

    this.endListForNextBillingCycle();
  }

  private initializeListForNextBillingCycle() {
    if (
      this.areSeatsChanged ||
      this.isExtraStoragePackageIncreased ||
      this.isExtraVAPackageIncreased
    ) {
      this.updates.push({ isEmptyLine: true });
    }

    this.updates.push({
      displayText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-13",
        { nextPaymentDate: this.nextPaymentDate }
      ),
      isStartList: true,
    });
  }

  private updateMessageWhenCanceledExtraStorageScheduled() {
    this.updates.push(
      {
        isStartItem: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-16"
        ),
      },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.updatedExtraStorage}TB`,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
        shouldDisplayBolded: true,
      },
      { isEndItem: true }
    );
  }

  private updateMessageWhenCanceledExtraVAsScheduled() {
    this.updates.push(
      {
        isStartItem: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-16"
        ),
      },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.updatedExtraVAs} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      { displayText: ` ${this.updatedExtraVAs * 50}`, shouldDisplayBolded: true },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      { isEndItem: true }
    );
  }

  private updateMessageWhenExtraStorageDecreased() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.currentExtraStorage}TB`,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
        shouldDisplayBolded: true,
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.updatedExtraStorage}TB`,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
        shouldDisplayBolded: true,
      },
      {
        isEndItem: true,
      }
    );

    this.info.push(
      {
        isEmptyLine: true,
      },
      {
        isEmptyLine: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-14"
        ),
      }
    );
  }

  private updateMessageWhenExtraVADecreased() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.currentExtraVAs} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      {
        displayText: ` ${this.currentExtraVAs * 50}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.updatedExtraVAs} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      {
        displayText: ` ${this.updatedExtraVAs * 50}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      {
        isEndItem: true,
      }
    );
  }

  private endListForNextBillingCycle() {
    this.updates.push({ isEndList: true });
  }

  private end() {}
}
